import { useEffect, useState } from 'react';
import {
  Button, Tooltip, Text, tokens,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { ChatSparkle20Regular, ChevronLeft20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import { useT } from '../../i18n';
import { SITE_INFO } from '../../config/siteInfo';

const TG_BLUE = '#229ED9';

interface TelegramHelpButtonProps {
  /** Clave de `t.telegramHelp.examples`: si existe, el diálogo muestra un carrusel con varios ejemplos */
  topic?: string;
  /** Ejemplo de texto/voz que el usuario le diría al bot para esta operación */
  voiceExample: string;
  /** Descripción breve de qué hace el bot con ese mensaje (opcional, tiene default genérico) */
  voiceDesc?: string;
  /** Comando de Telegram alternativo, si existe uno específico para este flujo (opcional) */
  command?: string;
  commandDesc?: string;
}

/**
 * Botón de ayuda + diálogo de ayuda que explica cómo registrar
 * la misma operación hablándole al bot de campo, en vez de usar el formulario.
 * Los textos del diálogo salen de `t.telegramHelp` (es/en/zh); cada pantalla pasa su ejemplo ya traducido.
 */
export function TelegramHelpButton({ topic, voiceExample, voiceDesc, command, commandDesc }: TelegramHelpButtonProps) {
  const [open, setOpen] = useState(false);
  const t = useT();
  const th = t.telegramHelp;
  const [idx, setIdx] = useState(0);

  const fromTopic = topic ? (th.examples as unknown as Record<string, readonly { voice: string; desc: string }[]>)[topic] : undefined;
  const examples = fromTopic?.length ? fromTopic : [{ voice: voiceExample, desc: voiceDesc ?? th.defaultDesc }];
  const current = examples[Math.min(idx, examples.length - 1)]!;

  useEffect(() => { if (open) setIdx(0); }, [open]);

  return (
    <>
      <Tooltip content={th.tooltip} relationship="description">
        <Button
          size="small"
          appearance="subtle"
          icon={<ChatSparkle20Regular aria-label={t.a11yLabels.telegram} />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>

      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface
          style={{ maxWidth: '480px' }}
          backdrop={{ style: { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.35)' } }}
        >
          <DialogBody>
            <DialogTitle>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: '36px', height: '36px', borderRadius: '50%', backgroundColor: TG_BLUE, color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <ChatSparkle20Regular />
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span>{th.title}</span>
                  <span style={{ fontSize: '12px', fontWeight: 400, color: TG_BLUE }}>{SITE_INFO.botUrl.replace('https://t.me/', '@')} · {th.botOnline}</span>
                </span>
              </span>
            </DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {th.intro}
                </Text>

                {command && (
                  <div style={{ backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium, padding: '10px' }}>
                    <Text weight="semibold" size={200}>{th.asCommand}</Text>
                    <Text block style={{ fontFamily: 'monospace', fontSize: '13px', marginTop: '4px' }}>{command}</Text>
                    {commandDesc && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{commandDesc}</Text>
                    )}
                  </div>
                )}

                <div style={{ backgroundColor: tokens.colorNeutralBackground3, borderRadius: '12px', padding: '12px' }}>
                  <Text weight="semibold" size={200}>{th.naturalLanguage}</Text>
                  <div aria-live="polite" style={{ minHeight: '120px', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    <div
                      style={{
                        alignSelf: 'flex-end', maxWidth: '85%', backgroundColor: TG_BLUE, color: '#fff',
                        borderRadius: '14px 14px 2px 14px', padding: '8px 12px', fontSize: '13px',
                      }}
                    >
                      {current.voice}
                    </div>
                    <div
                      style={{
                        alignSelf: 'flex-start', maxWidth: '85%', backgroundColor: tokens.colorNeutralBackground1,
                        border: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground1,
                        borderRadius: '14px 14px 14px 2px', padding: '8px 12px', fontSize: '12px',
                      }}
                    >
                      {current.desc}
                    </div>
                  </div>
                  {examples.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '6px' }}>
                      <Button
                        size="small" appearance="subtle" icon={<ChevronLeft20Regular />} aria-label="‹"
                        onClick={() => setIdx((idx - 1 + examples.length) % examples.length)}
                      />
                      {examples.map((_, i) => (
                        <button
                          key={i} type="button" aria-label={`${i + 1}/${examples.length}`}
                          onClick={() => setIdx(i)}
                          style={{
                            width: '8px', height: '8px', borderRadius: '50%', padding: 0, border: 'none', cursor: 'pointer',
                            backgroundColor: i === idx ? TG_BLUE : tokens.colorNeutralStroke1,
                          }}
                        />
                      ))}
                      <Button
                        size="small" appearance="subtle" icon={<ChevronRight20Regular />} aria-label="›"
                        onClick={() => setIdx((idx + 1) % examples.length)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>{th.gotIt}</Button>
              <Button
                as="a" href={SITE_INFO.botUrl} target="_blank" rel="noopener noreferrer"
                appearance="primary" style={{ backgroundColor: TG_BLUE, borderColor: TG_BLUE, color: '#fff' }}
              >
                {th.openBot}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
